import type { TableColumnType as ColumnType } from 'antd';
import moment from 'moment-timezone';
import React, { useCallback, useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import BackOfficeTable, {
  Props as BackOfficeTableProps
} from 'components/tables/BackOfficeTable';
import { EM_DASH } from 'constants/index';
import { SEARCH_PARAMS } from 'constants/search-params';
import { DATE_TIME_FORMAT } from 'constants/time';
import useTranslation from 'hooks/useTranslation';
import BackOfficeInvitationActionType from 'store/modules/backOfficeInvitations/constants';
import selectBackOfficeInviteesState from 'store/modules/backOfficeInvitations/selectors';
import { BackOfficeInvitee } from 'types/models/back-office-invitation';

const loadAction =
  BackOfficeInvitationActionType.LOAD_BACK_OFFICE_INVITATIONS_REQUEST;

const columns: ColumnType<BackOfficeInvitee>[] = [
  {
    title: 'Email',
    dataIndex: 'email',
    width: 250,
    sorter: true
  },
  {
    title: 'First Name',
    dataIndex: 'firstName',
    width: 150,
    sorter: true
  },
  {
    title: 'Last Name',
    dataIndex: 'lastName',
    width: 150,
    sorter: true
  },
  {
    title: 'Username',
    dataIndex: 'username',
    width: 150,
    sorter: true
  },
  {
    title: 'Mobile Number',
    dataIndex: 'mobileNumber',
    width: 200,
    sorter: true
  },
  {
    title: 'Landline Number',
    dataIndex: 'landlineNumber',
    width: 200,
    sorter: true
  },
  {
    title: 'Last Invited',
    dataIndex: 'lastInviteDate',
    width: 250,
    sorter: true,
    render: (lastInviteDate: number) =>
      lastInviteDate
        ? moment(lastInviteDate).format(DATE_TIME_FORMAT.DEFAULT)
        : EM_DASH
  }
];

export default function BackOfficeInvitationsTable({
  onPaginationChange
}: Pick<BackOfficeTableProps<BackOfficeInvitee>, 'onPaginationChange'>) {
  const { total, data } = useSelector(
    selectBackOfficeInviteesState,
    shallowEqual
  );

  const { t } = useTranslation();
  const localisedColumns = useMemo(
    () => columns.map((column) => {
      if (column.dataIndex === 'mobileNumber') {
        column.title = t('MOBILE_NUMBER');
      }
      return column;
    }),
    [t]
  );

  const [, setSearchParams] = useSearchParams();

  // If user exists, show edit user drawer by not appending email to the URL
  // else show invite/create user drawer by appending the invitee email.
  const onRowClick = useCallback(
    (id: string) => {
      const selectedInvitee = data.find((invitee) => invitee.id === id);
      setSearchParams((prev) => {
        const newSearchParams = new URLSearchParams(prev);
        newSearchParams.delete(SEARCH_PARAMS.DRAWER.key);
        if (selectedInvitee?.inviteeId) {
          newSearchParams.set(SEARCH_PARAMS.BACK_OFFICE.SELECTED_USER, String(selectedInvitee.inviteeId));
          newSearchParams.delete(SEARCH_PARAMS.INVITATION_EMAIL);
        } else {
          newSearchParams.delete(SEARCH_PARAMS.BACK_OFFICE.SELECTED_USER);
          if (selectedInvitee?.email) {
            newSearchParams.set(SEARCH_PARAMS.INVITATION_EMAIL, selectedInvitee.email);
          } else {
            newSearchParams.delete(SEARCH_PARAMS.INVITATION_EMAIL);
          }
        }
        return newSearchParams;
      });
    },
    [data, setSearchParams]
  );

  return (
    <BackOfficeTable<BackOfficeInvitee>
      columns={localisedColumns}
      data={data}
      total={total}
      loadActionType={loadAction}
      onRowClick={onRowClick}
      onPaginationChange={onPaginationChange}
      emptyText="No invitations found"
    />
  );
}
